import React, { useState, useEffect } from 'react';
import { getProviderLogo } from '@/services/providerService';
import { Skeleton } from '@/components/ui/skeleton';
import { ImageIcon } from 'lucide-react';

interface ProviderLogoProps {
  filename: string;
  alt: string;
  className?: string;
}

const ProviderLogo: React.FC<ProviderLogoProps> = ({ filename, alt, className = '' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [logoSrc, setLogoSrc] = useState<string | null>(null);

  useEffect(() => {
    const loadLogo = async () => {
      if (!filename) {
        setError(true);
        setLoading(false);
        return;
      }

      // If it's already a full URL, use it directly
      if (filename.startsWith('http')) {
        setLogoSrc(filename);
        setLoading(false);
        return;
      }

      try {
        const result = await getProviderLogo(filename);
        console.log(result)

        // Check if result exists and has the required properties
        if (result && result.mimetype && result.base64Data) {
          const imageUrl = `data:${result.mimetype};base64,${result.base64Data}`;
          setLogoSrc(imageUrl);
        } else {
          // Handle case where result is null or missing properties
          console.error('Invalid provider logo data received:', result);
          setError(true);
        }
      } catch (err) {
        console.error('Error loading provider logo:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadLogo();
  }, [filename]);

  if (loading) {
    return <Skeleton className={`${className} rounded-md`} />;
  }

  if (error || !logoSrc) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 rounded-md`}>
        <ImageIcon className="text-gray-400 h-8 w-8" />
      </div>
    );
  }

  return (
    <img
      src={logoSrc}
      alt={alt}
      className={`${className} object-contain max-w-full max-h-full`}
      loading="lazy"
    />
  );
};

export default ProviderLogo;